import React, { useState } from "react";
import { api } from "../lib/api";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Search, Phone, ExternalLink, LocateFixed, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Custom icons (inline SVG) — avoids broken default markers in webpack
const mkIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="transform:translate(-50%,-100%);">
      <svg width="34" height="46" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 0C7.6 0 0 7.6 0 17c0 13 17 29 17 29s17-16 17-29C34 7.6 26.4 0 17 0z" fill="${color}" stroke="#09090b" stroke-width="1.5"/>
        <circle cx="17" cy="17" r="6" fill="#fff"/>
      </svg>
    </div>`,
    iconSize: [34, 46],
    iconAnchor: [17, 46],
  });

const POLICE_ICON = mkIcon("#2563eb");
const ANIMAL_ICON = mkIcon("#dc2626");
const ORIGIN_ICON = mkIcon("#09090b");

function FlyTo({ center }) {
  const map = useMap();
  React.useEffect(() => {
    if (center) map.flyTo(center, 12, { duration: 0.8 });
  }, [center, map]);
  return null;
}

export default function NearbyStations() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [origin, setOrigin] = useState(null); // {lat,lng,label}
  const [stations, setStations] = useState([]);
  const [filter, setFilter] = useState("all");
  const [errorMsg, setErrorMsg] = useState("");
  const [searched, setSearched] = useState(false);

  const geocodeAndFind = async (q) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const { data: geos } = await api.get("/geocode", { params: { q } });
      if (!geos.length) {
        const m = `We couldn't recognize "${q}". Try a ZIP code, or a "City, ST" format (e.g. "Gardena, CA").`;
        setErrorMsg(m);
        toast.error("Location not recognized");
        setSearched(true);
        setOrigin(null);
        setStations([]);
        return;
      }
      const first = geos[0];
      let stationsData = [];
      try {
        const { data } = await api.get("/stations/nearby", {
          params: { lat: first.lat, lng: first.lng, radius_m: 20000 },
          timeout: 30000,
        });
        stationsData = data.stations || [];
      } catch (stationErr) {
        const detail = stationErr?.response?.data?.detail;
        const m = detail || "The nearby-stations service is temporarily unavailable. Please try again in a moment.";
        setErrorMsg(m);
        toast.error("Nearby stations lookup failed");
        // Still show the origin on the map so the user can see we recognized their location
        setOrigin({ lat: first.lat, lng: first.lng, label: first.display_name });
        setStations([]);
        setSearched(true);
        return;
      }
      setOrigin({ lat: first.lat, lng: first.lng, label: first.display_name });
      setStations(stationsData);
      setSearched(true);
      if (!stationsData.length) {
        toast("No stations found near this location. Try widening the search area.");
      }
    } catch (err) {
      const m = "Search failed. Please check your connection and try again.";
      setErrorMsg(m);
      toast.error(m);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 2) {
      setErrorMsg("Please enter a ZIP code or a city and state (e.g. 90247 or Austin, TX).");
      return;
    }
    geocodeAndFind(q);
  };

  const useMyLocation = () => {
    setErrorMsg("");
    if (!navigator.geolocation) {
      setErrorMsg("This device does not support automatic location. Enter a ZIP or city + state instead.");
      toast.error("Geolocation not supported on this device.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const o = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            label: "My current location",
          };
          const { data } = await api.get("/stations/nearby", {
            params: { lat: o.lat, lng: o.lng, radius_m: 15000 },
            timeout: 30000,
          });
          setOrigin(o);
          setStations(data.stations || []);
          setSearched(true);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          setErrorMsg(detail || "Could not fetch nearby stations. Please try again in a moment.");
          toast.error("Could not fetch nearby stations.");
        } finally {
          setLoading(false);
        }
      },
      (geoErr) => {
        const msg = geoErr.code === 1
          ? "Location permission denied. Please allow location access in your browser or enter a ZIP code instead."
          : "Could not detect your location. Please enter a ZIP code or city + state.";
        setErrorMsg(msg);
        toast.error("Location access denied.");
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const filtered = stations.filter((s) =>
    filter === "all" ? true : s.type === filter
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="nearby-page">
      <div className="label-eyebrow">Section · 03 · Direct Help</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
        Find the nearest police
        <br />
        or animal control.
      </h1>
      <p className="text-zinc-700 mt-4 max-w-xl leading-relaxed">
        Powered by OpenStreetMap. Search any US city, address or ZIP — or use
        your current location.
      </p>

      <form
        onSubmit={onSubmit}
        className="mt-8 border border-zinc-900 bg-white flex flex-col md:flex-row"
        data-testid="nearby-form"
      >
        <div className="flex items-center gap-3 px-4 py-4 flex-1 border-b md:border-b-0 md:border-r border-zinc-900">
          <Search size={18} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter city, state or ZIP (e.g. Austin, TX)"
            className="flex-1 bg-transparent outline-none text-base"
            data-testid="nearby-search-input"
          />
        </div>
        <button
          type="button"
          onClick={useMyLocation}
          className="px-5 py-4 border-b md:border-b-0 md:border-r border-zinc-900 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-100 inline-flex items-center gap-2 justify-center"
          data-testid="nearby-use-location"
        >
          <LocateFixed size={14} /> My Location
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-4 bg-zinc-900 text-white text-sm font-semibold uppercase tracking-wider hover:bg-red-600 inline-flex items-center gap-2 justify-center disabled:opacity-60"
          data-testid="nearby-submit"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {loading ? "Searching" : "Search"}
        </button>
      </form>

      {errorMsg && (
        <div
          className="mt-8 border border-red-600 bg-red-50 text-red-900 p-4 flex items-start gap-3"
          data-testid="nearby-error"
          role="alert"
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm leading-relaxed">{errorMsg}</div>
        </div>
      )}

      {!origin && !loading && !errorMsg && (
        <div className="mt-12 border border-dashed border-zinc-300 p-10 text-center">
          <MapPin size={32} className="mx-auto text-zinc-400" />
          <div className="font-display font-bold text-xl mt-3">
            Search to see results
          </div>
          <div className="text-sm text-zinc-500 mt-1">
            Try: "Chicago, IL" or "90210" or use your current location.
          </div>
        </div>
      )}

      {origin && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="flex items-center gap-2 mb-4">
              {[
                { k: "all", l: "All" },
                { k: "police", l: "Police" },
                { k: "animal_control", l: "Animal Control" },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  className={`px-3 py-2 text-[11px] uppercase tracking-widest font-semibold border ${
                    filter === t.k
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "border-zinc-300 hover:border-zinc-900"
                  }`}
                  data-testid={`nearby-filter-${t.k}`}
                >
                  {t.l}
                </button>
              ))}
              <div className="ml-auto label-eyebrow" data-testid="nearby-result-count">
                {filtered.length} found
              </div>
            </div>
            <div className="border border-zinc-900 max-h-[520px] overflow-y-auto" data-testid="nearby-list">
              {filtered.length === 0 ? (
                <div className="p-8 text-center">
                  <AlertTriangle size={24} className="mx-auto text-zinc-400" />
                  <div className="label-eyebrow mt-3">No results</div>
                  <div className="text-sm text-zinc-600 mt-1">
                    Try broadening your search area or switching filters.
                  </div>
                </div>
              ) : (
                filtered.map((s) => (
                  <div
                    key={s.id}
                    className="p-4 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 cursor-pointer"
                    onClick={() => setOrigin({ ...origin })}
                    data-testid={`station-${s.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-9 h-9 flex items-center justify-center border ${
                          s.type === "police"
                            ? "bg-blue-600 border-blue-700 text-white"
                            : "bg-red-600 border-red-700 text-white"
                        }`}
                      >
                        <MapPin size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-base truncate">
                          {s.name}
                        </div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                          {s.type === "police" ? "Police Station" : "Animal Shelter / Control"} · {s.distance_km} km
                        </div>
                        {s.address && (
                          <div className="text-xs text-zinc-600 mt-1 truncate">
                            {s.address}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-3 mt-2">
                          {s.phone && (
                            <a
                              href={`tel:${s.phone}`}
                              className="text-xs font-semibold inline-flex items-center gap-1 hover:text-red-600"
                            >
                              <Phone size={12} /> {s.phone}
                            </a>
                          )}
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-semibold inline-flex items-center gap-1 hover:text-red-600"
                          >
                            <ExternalLink size={12} /> Directions
                          </a>
                          {s.website && (
                            <a
                              href={s.website}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs font-semibold inline-flex items-center gap-1 hover:text-red-600"
                            >
                              <ExternalLink size={12} /> Website
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="border border-zinc-900 overflow-hidden">
              <MapContainer
                center={[origin.lat, origin.lng]}
                zoom={12}
                style={{ height: "560px", width: "100%" }}
                scrollWheelZoom
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FlyTo center={[origin.lat, origin.lng]} />
                <Marker position={[origin.lat, origin.lng]} icon={ORIGIN_ICON}>
                  <Popup>
                    <strong>Search origin</strong>
                    <br />
                    {origin.label}
                  </Popup>
                </Marker>
                {filtered.map((s) => (
                  <Marker
                    key={s.id}
                    position={[s.lat, s.lng]}
                    icon={s.type === "police" ? POLICE_ICON : ANIMAL_ICON}
                  >
                    <Popup>
                      <div className="space-y-1">
                        <strong>{s.name}</strong>
                        <div className="text-xs uppercase">{s.type === "police" ? "Police" : "Animal Control"}</div>
                        {s.address && <div className="text-xs">{s.address}</div>}
                        {s.phone && (
                          <a href={`tel:${s.phone}`} className="text-xs underline">
                            {s.phone}
                          </a>
                        )}
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`}
                          target="_blank"
                          rel="noreferrer"
                          className="block text-xs underline"
                        >
                          Get directions
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
